import { Outlet, NavLink, Link } from 'react-router-dom';
import { LogOut, Lock, Loader2, AlertCircle } from 'lucide-react';
import { Toaster, ErrorBoundary, Button, AppHeader, useMsalAccount } from '@higgins/ui';
import { msalInstance, signOut } from '@/lib/msal';
import { useMe } from '@/lib/hooks';
import { sendFeedback } from '@/lib/api';
import helpContent from '../../docs/USERS.md?raw';

const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

interface NavItem { to: string; label: string }

/** The app's section links, as pills. Rendered in the toolbar on wide screens, under the bar on phones. */
function SectionNav({ items, className }: { items: NavItem[]; className: string }) {
  return (
    <nav className={`items-center gap-1 text-sm ${className}`}>
      {items.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/'}
          className={({ isActive }) =>
            `shrink-0 px-3 py-1.5 rounded-md transition-colors font-medium ${
              isActive
                ? 'bg-orange-100 text-orange-700'
                : 'text-slate-600 hover:text-slate-900 hover:bg-accent'
            }`
          }
        >
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const account = useMsalAccount(msalInstance);
  const { data: me, isLoading: meLoading, isError: meFailed } = useMe();
  const isAdmin = IS_MOCK || (me?.roles?.includes('Admin') ?? false);
  // Admin inherits Builder access (server-side role hierarchy), so show Builder to both.
  const canBuild = isAdmin || (me?.roles?.includes('Builder') ?? false);
  // Entry is the portal's rule (#704): /api/me says whether it admits this user.
  const hasAccess = IS_MOCK || (me?.entitled ?? false);

  const navItems = [
    { to: '/',                label: 'Reports',   show: hasAccess },
    { to: '/combined',        label: 'Builder',   show: canBuild },
    { to: '/admin/templates', label: 'Templates', show: isAdmin },
    { to: '/admin/users',     label: 'Users',     show: isAdmin },
  ].filter(item => item.show);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-orange-50">
      <AppHeader
        title="GM Report System"
        subtitle="Monthly Reporting"
        containerClassName="max-w-7xl"
        homeLink={(p) => <Link to="/" {...p} />}
        feedback={{ send: sendFeedback, accountId: account?.localAccountId, appKey: 'gm-report-system' }}
        help={{ source: helpContent, title: 'GM Report System — Help' }}
        // Below lg (phones, tablets, narrow windows) the sections drop to a row under the bar —
        // beside the title they squeeze it to "G…". From lg up there is room for both.
        // No sections (a user with no role) → no row at all, not an empty strip.
        subNav={
          navItems.length > 0 && <SectionNav items={navItems} className="flex lg:hidden overflow-x-auto pb-3" />
        }
      >
        <div className="flex items-center gap-3">
          <SectionNav items={navItems} className="hidden lg:flex" />
          {account && (
            <div className="flex items-center gap-2 lg:pl-3 lg:border-l border-slate-200">
              <span className="text-sm text-slate-600 hidden sm:block">{account.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                title="Sign out"
                className="text-slate-500 hover:text-slate-900"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </AppHeader>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <ErrorBoundary>
          {!IS_MOCK && meLoading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking your access…
            </div>
          ) : hasAccess ? (
            <Outlet />
          ) : meFailed ? (
            <AccessCheckFailed />
          ) : (
            <NoAccess email={account?.username} />
          )}
        </ErrorBoundary>
      </main>

      <Toaster position="bottom-right" />
    </div>
  );
}

function NoAccess({ email }: { email?: string }) {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-5">
        <Lock className="w-7 h-7 text-muted-foreground" />
      </div>
      <h2 className="text-xl font-bold text-slate-900">You don't have access yet</h2>
      <p className="text-sm text-muted-foreground mt-2">
        {email ? <>You're signed in as <strong>{email}</strong>, but this </> : <>This </>}
        account isn't in a team that uses the GM Report System.
      </p>
      <p className="text-sm text-muted-foreground mt-3">
        Ask the Help Desk for access. Who can open each app is set on the Higgins Apps portal.
      </p>
    </div>
  );
}

/** /api/me failed (the access check or the API was down): say so, never "no access". */
function AccessCheckFailed() {
  return (
    <div className="max-w-lg mx-auto text-center py-16">
      <AlertCircle className="w-7 h-7 text-destructive mx-auto mb-5" />
      <h2 className="text-xl font-bold text-slate-900">Couldn't check your access</h2>
      <p className="text-sm text-muted-foreground mt-2">
        Something went wrong checking who can use the GM Report System. Reload the page in a minute.
      </p>
    </div>
  );
}
